import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Hook for generating a stable React key that updates only when formData changes externally.
 *
 * This hook solves a critical issue with RJSF (React JSON Schema Form) UI not updating when
 * formData is modified externally (e.g., by variable sharing or array synchronization). The
 * issue occurs because RJSF internally manages its own form state and only updates the UI
 * during its own onChange cycle. When formData is changed externally, RJSF receives the new
 * prop but doesn't re-render its UI to reflect the changes.
 *
 * ## Problem Context
 * When multiple ConfigDrivenContentEditor instances exist (desktop + mobile flavors):
 * - User edits one flavor → onChange fires → formData updates
 * - Variable sharing/array sync propagates to other flavor → formData updates
 * - But the other flavor's RJSF UI doesn't refresh (RJSF doesn't update UI for external formData changes)
 *
 * ## Solution Approach
 * Generate a unique key that forces remounting, but ONLY for external changes:
 * - **External change**: formData updates WITHOUT onChange being called first
 * - **User change**: formData updates AFTER onChange was called
 *
 * This preserves form state during user typing while refreshing UI for external updates.
 *
 * ## Usage Pattern
 * ```tsx
 * // With default key prefix
 * const { key, markUserChange } = useExternalFormDataKey(formData);
 *
 * // With custom key prefix for disambiguation
 * const { key, markUserChange } = useExternalFormDataKey(formData, 'flavor-0');
 *
 * const onChange = useCallback((data) => {
 *   markUserChange(); // Signal this is a user-initiated change
 *   // ... rest of onChange logic
 * }, [markUserChange]);
 *
 * return <ConfigDrivenFormComponent key={key} onChange={onChange} {...props} />;
 * ```
 *
 * ## Implementation Details
 * - Uses ref to track onChange calls (doesn't cause re-renders)
 * - Tracks previous formData to detect actual changes
 * - Increments generation counter only for external changes
 * - Memoizes callback to prevent unnecessary re-renders
 * - Supports optional key prefix for multiple instances on same page
 *
 * @param formData - The current form data to monitor for changes
 * @param keyPrefix - Optional unique prefix for the key (default: 'form-key'). Use this when multiple instances exist on the same page.
 * @returns Object with:
 *   - `key`: string for React key prop (format: "{keyPrefix}-{generation}")
 *   - `markUserChange`: callback to invoke when onChange fires
 *
 * @example
 * ```tsx
 * const { key, markUserChange } = useExternalFormDataKey(formData, `flavor-${flavorIndex}`);
 *
 * const onChange = useCallback((data) => {
 *   markUserChange(); // Call FIRST to mark as user change
 *   formApi.change(getContent('content'), data.formData);
 *   // ... other onChange logic
 * }, [markUserChange, formApi, getContent]);
 *
 * return (
 *   <ConfigDrivenFormComponent
 *     key={key}
 *     formData={formData}
 *     onChange={onChange}
 *   />
 * );
 * ```
 */
export function useExternalFormDataKey(
  formData: unknown,
  keyPrefix = 'form-key',
): {
  key: string;
  markUserChange: () => void;
} {
  const previousFormDataRef = useRef<unknown>(formData);
  const onChangeCalledRef = useRef(false);
  const [keyGeneration, setKeyGeneration] = useState(0);
  const markUserChange = useCallback(() => {
    onChangeCalledRef.current = true;
  }, []);

  useEffect(() => {
    // Only process if formData actually changed (different reference)
    if (previousFormDataRef.current !== formData) {
      // If formData changed but onChange wasn't called, it's an external change
      // therefore, increment key generation to force remount
      if (!onChangeCalledRef.current) setKeyGeneration((prev) => prev + 1);

      previousFormDataRef.current = formData;
      onChangeCalledRef.current = false;
    }
  }, [
    formData,
  ]);

  return {
    key: `${keyPrefix}-${keyGeneration}`,
    markUserChange,
  };
}
